import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * Returns the effective role for the current user.
 *
 * For `employee` users, the actual behavior depends on who created them:
 *   - company_type = 'partner'           → behaves like 'partner'
 *   - company_type = 'restaurant_company' → behaves like 'restaurant_company'
 *
 * For all other roles the value equals authStore.user.role directly.
 *
 * @example
 * const { effectiveRole, isPartnerLevel, isCompanyLevel } = useEffectiveRole()
 *
 * if (isPartnerLevel.value) { ... }  // true for partner + partner-employee
 * if (isCompanyLevel.value) { ... }  // true for restaurant_company + company-employee
 */
export const useEffectiveRole = () => {
  const authStore = useAuthStore()

  const effectiveRole = computed(() => {
    const role = authStore.user?.role
    if (role !== 'employee') return role ?? null
    return authStore.user?.companyType ?? 'employee'
  })

  /** True for roles that should see/provide a partner_id (super_admin sees picker; partner auto-fills) */
  const showPartnerDropdown = computed(() =>
    effectiveRole.value !== 'partner' &&
    effectiveRole.value !== 'restaurant_company' &&
    effectiveRole.value !== 'employee'
  )

  /** True for roles that should see/provide a company_id */
  const showCompanyDropdown = computed(() =>
    effectiveRole.value !== 'restaurant_company' &&
    effectiveRole.value !== 'employee'
  )

  /** True when the user behaves like a partner (partner or partner-created employee) */
  const isPartnerLevel = computed(() => effectiveRole.value === 'partner')

  /** True when the user behaves like a restaurant_company (rc or rc-created employee) */
  const isCompanyLevel = computed(() => effectiveRole.value === 'restaurant_company')

  return {
    effectiveRole,
    showPartnerDropdown,
    showCompanyDropdown,
    isPartnerLevel,
    isCompanyLevel,
  }
}
